import { memo, useState } from 'react'
import { useRouter } from 'next/compat/router'
import Link from 'next/link'
import { useTranslation } from 'next-i18next'
import styled, { css } from 'styled-components'
import {
  Box,
  Divider,
  Skeleton,
  Stack,
  Text,
  useDialogController
} from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import noop from 'lodash/noop'
import toNumber from 'lodash/toNumber'

import { colors } from '@/styles'
import { isEmptyOrNull } from '@/utils'
import { getUrl, isGroup } from '@/utils/listings'
import DSLoginModal from '@/features/Auth/login-modal/ds-login-modal'
import BackgroundPhotoSlider from '@/components/background-photo-slider'
import CornerRibbon from '@/components/corner-ribbon'
import FavouriteButton from '@/components/favourite-button'
import ListingTypeText from '@/components/listing-type-text'
import { PillWrapper, VerifiedPill } from '@/components/pill'

import { ListingCardActions } from './actions'
import * as styles from './ListingCard.css'
import PriceDisplay from './pricing-display'
import {
  getClickEventId,
  getIsRented,
  getListingCardDlv,
  getPrice,
  renderAddress
} from './utils'

const Address = ({ children }) => {
  return (
    <Text weight="strong" size="small" maxLines={1}>
      {children}
    </Text>
  )
}

const SecondaryDetails = ({ children }) => {
  return (
    <Text weight="regular" size="xsmall" maxLines={1}>
      {children}
    </Text>
  )
}

const DetailRow = ({ children }) => {
  return (
    <Box display="flex" alignItems="center" gap="xxxsmall">
      {children}
    </Box>
  )
}

const DetailText = ({ children, size }) => {
  return (
    <Text tone="secondary" weight="medium" maxLines={1} size={size}>
      {children}
    </Text>
  )
}

const FavouriteButtonStyled = styled(FavouriteButton)`
  z-index: 10;

  svg {
    width: 22px;
    height: 20px;
  }

  :hover {
    svg {
      transition: transform 0.2s;
      transform: scale(1.05);
    }
  }
`

const FeaturedPrice = ({ children, skeleton }) => {
  return (
    <Box
      paddingX="xxsmall"
      paddingY="xxxsmall"
      position="absolute"
      left={0}
      bottom={0}
      background={skeleton ? 'disabled' : 'bodyDark'}
      display="flex"
      justifyContent="center"
      className={styles.featuredPrice}
    >
      {children}
    </Box>
  )
}

const priceStyles = css`
  color: ${colors.white};
`

const frequencyStyles = css`
  color: ${colors.white};
  font-size: 14px;
  font-weight: 500;
`

const AvailableDateText = styled.div`
  margin: 4px auto;
  color: ${colors.primary};
  font-size: 14px;
  font-weight: 500;
`

const ExtAddress = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: ${colors.grey08};
`

const ExtSecondaryDetails = styled.div`
  font-size: 14px;
  color: ${vars.foregroundColor.secondary};
`

const photoConfig = {
  fit: 'fit-in/500x300',
  quality: 80
}

const PrimaryDetails = memo(
  ({
    small,
    listing,
    loadingOrSkeleton,
    unitCount,
    bedroomCount,
    bathroomCount,
    listingSize
  }) => {
    const { t } = useTranslation()
    const textSize = small ? 'small' : 'standard'
    return (
      <>
        {loadingOrSkeleton ? (
          <DetailRow>
            <Skeleton.Shape width={160} height={18} />
          </DetailRow>
        ) : (
          <DetailRow>
            {listing.unit_type_scope_txt_id?.toLowerCase() === 'single' ? (
              <DetailText size={textSize} id="listing-card-bed">
                {t('common.room', 'Room')}
              </DetailText>
            ) : (
              <>
                {isGroup(listing) && unitCount > 0 ? (
                  <DetailText size={textSize} id="listing-card-groupcount">
                    {t('b.listing.view.count_units_available.text', {
                      defaultValue: '{{ count }} units available',
                      count: unitCount
                    })}
                  </DetailText>
                ) : (
                  <>
                    <DetailText size={textSize} id="listing-card-bed">
                      {bedroomCount > 0
                        ? t('b.listing.view.count_beds.text', {
                            defaultValue: '{{ bedroomCount }} Bed',
                            bedroomCount
                          })
                        : t('common.studio', 'Studio')}
                    </DetailText>
                    <DetailText size={textSize}>·</DetailText>
                    <DetailText size={textSize} id="listing-card-bath">
                      {t('b.listing.view.count_baths.text', {
                        defaultValue: '{{ bathroomCount }} Bath',
                        bathroomCount
                      })}
                    </DetailText>
                    {listingSize > 0 && (
                      <>
                        <DetailText size={textSize}>·</DetailText>
                        <DetailText size={textSize} id="listing-card-size">
                          {t('b.listing.view.size.text', {
                            defaultValue: '{{ listingSize }} ft²',
                            listingSize
                          })}
                        </DetailText>
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </DetailRow>
        )}
      </>
    )
  }
)

PrimaryDetails.displayName = 'PrimaryDetails'

const ListingCardInner = ({
  listing = {},
  featured = false,
  featuredPosition = '',
  onClick = noop,
  onComplete = noop,
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  listingImageConfig = photoConfig,
  variant = 'regular',
  favouriteButton = false,
  loading = false,
  customFooter,
  noSchema = false,
  noButtons = false,
  skeleton = false,
  customBaseUrl = '',
  trackImpression = {},
  ...rest
}) => {
  const isRow = variant === 'row'
  const { t } = useTranslation()
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(1)
  const {
    visible: loginDSModalVisible,
    showModal: showDSLoginModal,
    closeModal: closeDSLoginModal
  } = useDialogController()

  const loadingOrSkeleton = loading || skeleton
  const bedroomCount = toNumber(listing.bedrooms)
  const bathroomCount = toNumber(listing.bathrooms)
  const listingSize = toNumber(listing.size)
  const unitCount = toNumber(listing.gr_count)
  const unitType = listing.unit_type_txt_id
  const unitScope = listing.unit_type_scope_txt_id
  const isRented = getIsRented(listing)
  const showType = !!unitType && !!unitScope

  const isVerified = listing.verified_state_machine === 'approved'
  const unitImageFiles = listing.unit_files?.filter(
    file => !file.tag?.includes('video')
  )
  const isStreetNumberHidden =
    !!Number(listing.hide_unit_number) && listing.unit_type_txt_id === 'HOUSE'

  // For similar listings, different data is returned
  const cover =
    listing.cover_photo?.aws_s3_key || listing.cover_photo_aws_s3_key
  const images = !isEmptyOrNull(unitImageFiles)
    ? unitImageFiles
    : [{ aws_s3_key: cover }]
  const photoCount = images.length

  const BORDER_RADIUS = 12

  const router = useRouter()

  const isCustomCompanyPage = router?.pathname?.includes('/ext/company')

  const listingLink = customBaseUrl
    ? `${customBaseUrl}${getUrl(listing)}`
    : getUrl(listing)

  const handleOnClick = () => {
    onClick()
    window.dataLayer.push({
      event: getClickEventId(listing, featured, featuredPosition),
      listingCard: getListingCardDlv(listing, featured)
    })
  }

  const getUnitType = () => {
    if (listing.unit_type_scope_txt_id === 'SINGLE') return 'Room'
    else if (listing.unit_type_txt_id === 'CONDO') return 'ApartmentComplex'
    else if (listing.unit_type_txt_id === 'HOUSE') return 'House'
    else return 'Accommodation'
  }
  const getListingUrl = () => {
    const listingUrl = getUrl({
      building_type: listing.unit_type_txt_id?.toLowerCase(),
      listing_id: listing.listing_id,
      city: listing.city
    })
    return `https://liv.rent${listingUrl}`
  }
  const getListingSchema = () =>
    JSON.stringify(
      isStreetNumberHidden
        ? {
            '@context': 'http://schema.org',
            '@type': getUnitType(),
            name: getListingName(),
            url: getListingUrl(),
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'CA',
              addressLocality: listing.city,
              addressRegion: listing.state,
              name: `${listing.street_name}, ${listing.city}, ${listing.state}`,
              streetAddress: listing.street_name
            },
            containedInPlace: {
              '@type': 'City',
              url: `https://liv.rent/rental-listings/city/${listing.city?.toLowerCase()}`,
              name: listing.city,
              address: {
                '@type': 'PostalAddress',
                addressCountry: 'CA',
                addressLocality: listing.city,
                addressRegion: listing.state
              }
            }
          }
        : {
            '@context': 'http://schema.org',
            '@type': getUnitType(),
            name: getListingName(),
            url: getListingUrl(),
            address: {
              '@type': 'PostalAddress',
              addressCountry: 'CA',
              addressLocality: listing.city,
              addressRegion: listing.state,
              name: `${listing.full_street_name}, ${listing.city}, ${listing.state}`,
              postalCode: listing.zip,
              streetAddress: listing.full_street_name
            },
            geo: {
              '@type': 'GeoCoordinates',
              latitude: listing.location?.lat,
              longitude: listing.location?.lon
            },
            containedInPlace: {
              '@type': 'City',
              url: `https://liv.rent/rental-listings/city/${listing.city?.toLowerCase()}`,
              name: listing.city,
              address: {
                '@type': 'PostalAddress',
                addressCountry: 'CA',
                addressLocality: listing.city,
                addressRegion: listing.state
              }
            }
          }
    )
  const getListingName = () => {
    const addressText = `for Rent at ${
      isStreetNumberHidden ? listing.street_name : listing.full_street_name
    }, ${listing.city}, ${listing.state}`
    if (listing.unit_type_scope_txt_id === 'SINGLE')
      return `Room ${addressText}`
    else if (listing.unit_type_txt_id === 'CONDO')
      return `Apartments ${addressText}`
    else if (listing.unit_type_txt_id === 'HOUSE') return `House ${addressText}`
    else return `Townhouse ${addressText}`
  }

  const AvailableDate = () => {
    const { availability_date } = listing
    const inputDate = new Date(availability_date)
    const today = new Date()

    const isAvailableNow = inputDate < today
    const formattedDate = isAvailableNow
      ? 'Available Now ⚡'
      : `Available on ${inputDate.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        })}`

    return formattedDate
  }

  if (loadingOrSkeleton) {
    return (
      <Box
        className={[
          styles.box[variant],
          variant !== 'university' &&
            (noButtons || isRented) &&
            styles.noButtons,
          rest.className
        ]}
        borderRadius="xlarge"
        style={{
          overflow: 'hidden'
        }}
      >
        <Box>
          <Skeleton.Shape width="100%" height="100%" borderRadius="none" />
        </Box>
        <Box
          padding="small"
          display="flex"
          flexDirection="column"
          justifyContent="spaceBetween"
        >
          <Stack space="xxsmall">
            <Skeleton.Shape width="80%" height={18} />
            <Skeleton.Shape width="60%" height={18} />
          </Stack>

          {!featured && (
            <Stack space="xxsmall">
              <Skeleton.Shape width="40%" height={18} />
              <Skeleton.Shape width="50%" height={18} />
            </Stack>
          )}

          <Skeleton.Shape width="100%" height={40} />
        </Box>
      </Box>
    )
  }

  return (
    <>
      <DSLoginModal
        mode={'login'}
        eventId={'web-signup-submit-savebutton'}
        visible={loginDSModalVisible}
        onClose={closeDSLoginModal}
        t={t}
        listingId={listing.listing_id}
      />
      <Link href={skeleton ? '#' : listingLink} passHref legacyBehavior>
        <Box
          component="a"
          width="full"
          borderRadius="xlarge"
          target="_blank"
          pointerEvents={loadingOrSkeleton ? 'none' : undefined}
          onClick={handleOnClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          id={getClickEventId()}
          overflow="hidden"
          role="group"
          aria-label={`Most relevant listing ${listing.listing_id}`}
          data-impression={trackImpression.enabled}
          data-impression-listingid={listing.listing_id}
          data-impression-page={trackImpression.page}
          data-impression-section={trackImpression.section}
          data-impression-sponsored={trackImpression.sponsored}
          {...rest}
          className={[
            styles.box[variant],
            variant !== 'university' &&
              (noButtons || isRented) &&
              styles.noButtons,
            rest.className
          ]}
        >
          {/* Topper image section */}
          <Box
            position="relative"
            background="transparent"
            overflow="hidden"
            width="full"
            className={styles.imageContainer}
          >
            <BackgroundPhotoSlider
              images={images}
              imageConfig={listingImageConfig}
              borderRadius={BORDER_RADIUS}
              setCurrentPhotoIndex={setCurrentPhotoIndex}
              isRow={isRow}
              altText={renderAddress(listing)}
            />
            {photoCount > 1 && (
              <Box
                position="absolute"
                justifyContent="center"
                color="white"
                display="flex"
                paddingX="xxsmall"
                paddingY="xxsmall"
                borderRadius="xlarge"
                className={styles.photoCount}
              >
                <Text size="xsmall">{`${currentPhotoIndex} / ${photoCount}`}</Text>
              </Box>
            )}
            <PillWrapper>{isVerified && <VerifiedPill />}</PillWrapper>
            {isRented && (
              <CornerRibbon>{t('common.rented', 'Rented')}</CornerRibbon>
            )}
            {!isRented && featured && (
              <CornerRibbon>
                {t('b.listings.view.sponsored.text', 'Sponsored')}
              </CornerRibbon>
            )}
            {featured && (
              <FeaturedPrice skeleton={skeleton}>
                {!loadingOrSkeleton && (
                  <PriceDisplay
                    price={getPrice(listing)}
                    frequency={listing.price_frequency}
                    fallback={isRented && t('common.rented', 'Rented')}
                    priceStyles={priceStyles}
                    frequencyStyles={frequencyStyles}
                    center
                  />
                )}
              </FeaturedPrice>
            )}
            {featured ||
              (isCustomCompanyPage && !isRented && (
                <FeaturedPrice>
                  <PriceDisplay
                    price={getPrice(listing)}
                    frequency={listing.price_frequency}
                    fallback={isRented && t('common.rented', 'Rented')}
                    priceStyles={priceStyles}
                    frequencyStyles={frequencyStyles}
                    center
                  />
                </FeaturedPrice>
              ))}
          </Box>
          <Box
            width="full"
            background="white"
            display="flex"
            flexDirection="column"
            padding="small"
            gap="medium"
            minWidth={0}
          >
            <Stack space="medium">
              {featured ? (
                <Box
                  display="flex"
                  justifyContent="spaceBetween"
                  alignItems="flexStart"
                  gap="xxxsmall"
                >
                  <Box minWidth={0}>
                    <Stack space="xsmall">
                      <Stack space="xxsmall">
                        <Address type="large" id="listing-card-address">
                          {renderAddress(listing)}
                        </Address>
                        <SecondaryDetails id="listing-card-type">
                          {`${listing.city}, ${listing.state}`}
                        </SecondaryDetails>
                      </Stack>

                      <PrimaryDetails
                        bedroomCount={bedroomCount}
                        bathroomCount={bathroomCount}
                        listingSize={listingSize}
                        unitCount={unitCount}
                        isCustomCompanyPage={isCustomCompanyPage}
                        loadingOrSkeleton={loadingOrSkeleton}
                        listing={listing}
                        small
                      />
                    </Stack>
                  </Box>

                  {favouriteButton && !isRented && (
                    <FavouriteButtonStyled
                      title="Add to Favourites"
                      aria-label="Add to Favourites"
                      listingId={listing.listing_id}
                      eventId="listing-card-click-save"
                      noShadow
                      iconColor={colors.blue16}
                      offColor={colors.blue16}
                      showDSLoginModal={showDSLoginModal}
                      listingCardDlv={getListingCardDlv(listing, featured)}
                    />
                  )}
                </Box>
              ) : (
                <>
                  {!isCustomCompanyPage && (
                    <Stack space="xxsmall">
                      <Box
                        display="flex"
                        alignItems="flexEnd"
                        justifyContent="flexStart"
                      >
                        <PriceDisplay
                          price={getPrice(listing)}
                          frequency={listing.price_frequency}
                          fallback={isRented && t('common.rented', 'Rented')}
                          loading={loadingOrSkeleton}
                        />
                        {!loadingOrSkeleton && favouriteButton && !isRented && (
                          <FavouriteButtonStyled
                            title="Add to Favourites"
                            aria-label="Add to Favourites"
                            listingId={listing.listing_id}
                            eventId="listing-card-click-save"
                            noShadow
                            iconColor={colors.blue16}
                            offColor={colors.blue16}
                            showDSLoginModal={showDSLoginModal}
                            listingCardDlv={getListingCardDlv(
                              listing,
                              featured
                            )}
                          />
                        )}
                      </Box>
                      <PrimaryDetails
                        bedroomCount={bedroomCount}
                        bathroomCount={bathroomCount}
                        listingSize={listingSize}
                        unitCount={unitCount}
                        isCustomCompanyPage={isCustomCompanyPage}
                        loadingOrSkeleton={loadingOrSkeleton}
                        listing={listing}
                      />
                    </Stack>
                  )}
                  {isCustomCompanyPage && (
                    <>
                      {!isRented && (
                        <AvailableDateText>{AvailableDate()}</AvailableDateText>
                      )}
                      <ExtAddress id="listing-card-address">
                        {renderAddress(listing)}
                      </ExtAddress>
                    </>
                  )}
                </>
              )}

              {!featured && (
                <>
                  {!isRow && <Divider />}
                  {/* Address row */}
                  {!isRow && (
                    <DetailRow>
                      {loadingOrSkeleton ? (
                        <Stack space="xxxsmall">
                          <Skeleton.Shape width={120} height={14} />
                          <Skeleton.Shape width={180} height={12} />
                        </Stack>
                      ) : !isCustomCompanyPage ? (
                        <Box minWidth={0}>
                          <Stack space="xxsmall">
                            <Address type="large" id="listing-card-address">
                              {renderAddress(listing)}
                            </Address>
                            <SecondaryDetails id="listing-card-type">
                              {`${listing.city}, ${listing.state}`}
                              {showType && (
                                <span>
                                  {' '}
                                  ·{' '}
                                  <ListingTypeText
                                    unitType={unitType}
                                    unitScope={unitScope}
                                  />
                                </span>
                              )}
                            </SecondaryDetails>
                          </Stack>
                        </Box>
                      ) : (
                        <>
                          <ExtSecondaryDetails>
                            {`${listing.city}, ${listing.state}`}
                            {showType && (
                              <span>
                                {' '}
                                ·{' '}
                                <ListingTypeText
                                  unitType={unitType}
                                  unitScope={unitScope}
                                />
                              </span>
                            )}
                          </ExtSecondaryDetails>
                          <PrimaryDetails
                            listing={listing}
                            bedroomCount={bedroomCount}
                            bathroomCount={bathroomCount}
                            listingSize={listingSize}
                            unitCount={unitCount}
                            small
                            isCustomCompanyPage
                            loadingOrSkeleton={loadingOrSkeleton}
                          />
                        </>
                      )}
                    </DetailRow>
                  )}
                  {customFooter}
                </>
              )}
            </Stack>

            {/* Control button row */}
            {!isRented && !noButtons && (
              <Box style={{ marginTop: 'auto' }}>
                <ListingCardActions
                  listing={listing}
                  isCustomCompanyPage={isCustomCompanyPage}
                  customBaseUrl={customBaseUrl}
                  loading={skeleton}
                  featured={featured}
                  stackButtons={isRow}
                  onComplete={() => {
                    onComplete()
                  }}
                />
              </Box>
            )}
          </Box>
          {(!noSchema || skeleton) && (
            <script
              defer
              async
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: getListingSchema()
              }}
            />
          )}
        </Box>
      </Link>
    </>
  )
}

const ListingCard = memo(ListingCardInner)
ListingCard.displayName = 'ListingCard'

export default ListingCard
